// components/MessageRow.tsx
import { useState } from 'react';
import type { MessageAnalysis } from '../balancing.types';
import { formatStatValue, getStatColor } from '../balancing.utils';
import { ResponseRow } from './ResponseRow';

type Props = {
  message: MessageAnalysis;
};

export function MessageRow({ message }: Props) {
  const [showResponses, setShowResponses] = useState(false);

  const stats = [
    { label: 'Cash', key: 'cash' as const },
    { label: 'Investments', key: 'investments' as const },
    { label: 'Happiness', key: 'happiness' as const },
  ];

  return (
      <div className="border border-gray-200 rounded-lg p-4 bg-white">
        {/* Message header */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold text-gray-500">
              Message #{message.messageIndex + 1}
            </span>
              {message.messageName && (
                  <span className="text-xs text-gray-400">({message.messageName})</span>
              )}
            </div>
            <p className="text-sm text-gray-700 line-clamp-2">{message.messageText}</p>
          </div>
          <div className="text-xs text-gray-500 whitespace-nowrap">
            {message.responseCount} {message.responseCount === 1 ? 'option' : 'options'}
          </div>
        </div>

        {/* Warning badges */}
        {(message.hasDeadChoice || message.maxVariance > 20) && (
            <div className="flex gap-2 mb-3">
              {message.hasDeadChoice && (
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
              ⓘ Dead Choice - All options identical
            </span>
              )}
              {message.maxVariance > 20 && (
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-700">
              ⚠ High Variance (Δ{message.maxVariance})
            </span>
              )}
            </div>
        )}

        {/* Stats table */}
        <table className="w-full mb-3">
          <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-1 px-2 text-xs font-semibold text-gray-700">Stat</th>
            <th className="text-center py-1 px-2 text-xs font-semibold text-gray-700">Min</th>
            <th className="text-center py-1 px-2 text-xs font-semibold text-gray-700">Avg</th>
            <th className="text-center py-1 px-2 text-xs font-semibold text-gray-700">Max</th>
          </tr>
          </thead>
          <tbody>
          {stats.map((stat) => (
              <tr key={stat.key} className="border-b border-gray-100 last:border-0">
                <td className="py-1 px-2 text-xs font-medium text-gray-900">{stat.label}</td>
                <td className="py-1 px-2 text-center">
                <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-bold ${getStatColor(message.min[stat.key])}`}>
                  {formatStatValue(message.min[stat.key])}
                </span>
                </td>
                <td className="py-1 px-2 text-center">
                <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-bold ${getStatColor(message.avg[stat.key])}`}>
                  {formatStatValue(message.avg[stat.key])}
                </span>
                </td>
                <td className="py-1 px-2 text-center">
                <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-bold ${getStatColor(message.max[stat.key])}`}>
                  {formatStatValue(message.max[stat.key])}
                </span>
                </td>
              </tr>
          ))}
          </tbody>
        </table>

        {/* Toggle button */}
        <button
            onClick={() => setShowResponses(!showResponses)}
            className="cursor-pointer  text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          {showResponses ? '▼' : '▶'} {showResponses ? 'Hide' : 'Show'} Response Details
        </button>

        {/* Response breakdown */}
        {showResponses && (
            <div className="space-y-2 pt-2 mt-2 border-t border-gray-200">
              {message.responses.map((response, idx) => (
                  <ResponseRow key={idx} response={response} />
              ))}
            </div>
        )}
      </div>
  );
}